import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import InvitacionesEnviadas from '@/components/team/InvitacionesEnviadas';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Phone, Check, X, UserPlus, Copy, ChevronDown, ChevronRight, Mail, MessageCircle, Link2, ShieldCheck } from 'lucide-react';
import useTenantPhoneCode from '@/hooks/useTenantPhoneCode';

const DELIVERY_METHODS = [
  { value: 'copy_link', label: 'Copiar enlace', icon: Link2 },
  { value: 'email', label: 'Enviar por correo', icon: Mail },
  { value: 'whatsapp', label: 'Enviar por WhatsApp', icon: MessageCircle },
];

const DELIVERY_STATUS_LABELS = {
  sent: { text: 'Enviado', className: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  failed: { text: 'No se pudo enviar — usa el enlace', className: 'text-amber-700 bg-amber-50 border-amber-200' },
  skipped_no_channel: { text: 'Sin WhatsApp registrado — usa el enlace', className: 'text-amber-700 bg-amber-50 border-amber-200' },
  skipped_entitlement_disabled: { text: 'Envío automático no disponible en tu plan', className: 'text-amber-700 bg-amber-50 border-amber-200' },
};

const COUNTRY_CODES = [
  { code: '+52', label: 'MX +52' }, { code: '+1', label: 'US/CA +1' },
  { code: '+57', label: 'CO +57' }, { code: '+56', label: 'CL +56' },
  { code: '+54', label: 'AR +54' }, { code: '+55', label: 'BR +55' },
  { code: '+34', label: 'ES +34' }, { code: '+593', label: 'EC +593' },
  { code: '+51', label: 'PE +51' }, { code: '+58', label: 'VE +58' },
];

const VISIBLE_ROLES = [
  { value: 'Vendedor', label: 'Vendedor' },
  { value: 'Asesor', label: 'Asesor' },
  { value: 'Recepción', label: 'Recepción' },
  { value: 'Gerente', label: 'Gerente' },
  { value: 'Cajero', label: 'Cajero (solo Punto de Venta)' },
];

// Sprint 76 (8/N) — antes no existía ningún botón para cambiar el rol de
// permisos de un miembro ni para activarlo/desactivarlo; los endpoints
// (PATCH /tenant/team/{id}/role y /status) existían completos sin UI.
// tenant_owner nunca aparece aquí — es intocable por diseño del backend.
const PERMISSION_ROLES = [
  { value: 'tenant_admin', label: 'Administrador' },
  { value: 'tenant_manager', label: 'Gerente' },
  { value: 'tenant_user', label: 'Vendedor' },
];

export default function TeamManager() {
  const { user } = useAuth();
  const tenantPhoneCode = useTenantPhoneCode();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  // Invite state
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', visible_role: 'Vendedor', phone_country_code: tenantPhoneCode, whatsapp_phone: '', is_lead_eligible_pending: false, delivery_method: 'copy_link' });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Pending invitations
  const [showPending, setShowPending] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);

  const canInvite = user?.tenant_role === 'tenant_owner' || user?.tenant_role === 'tenant_admin';
  const canHavePin = ['tenant_owner', 'tenant_admin', 'tenant_manager'].includes(user?.tenant_role);
  const canChangeEmail = ['tenant_owner', 'tenant_admin'].includes(user?.tenant_role);
  const canManageRoles = canInvite; // mismo permiso que invitar: owner/admin
  const [changingRoleId, setChangingRoleId] = useState('');
  const [changingStatusId, setChangingStatusId] = useState('');

  // S56 (1/N) — PIN de autorización: solo el propio usuario lo configura,
  // nunca el de otro miembro del equipo (por eso vive aparte de la tabla).
  const [showPinForm, setShowPinForm] = useState(false);
  const [pinForm, setPinForm] = useState({ current_password: '', new_pin: '' });
  const [savingPin, setSavingPin] = useState(false);
  const myMember = members.find(m => m.id === user?.id);

  const savePin = async () => {
    setSavingPin(true);
    try {
      await api.post('/tenant/business/team/supervisor-pin', pinForm);
      toast.success('PIN de autorización guardado');
      setShowPinForm(false);
      setPinForm({ current_password: '', new_pin: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingPin(false); }
  };

  const load = () => {
    // Sprint 76 (8/N) — include_inactive=true para que un miembro
    // desactivado siga visible aquí con un botón para reactivarlo; sin
    // esto, desactivar era una puerta sin regreso desde esta pantalla.
    api.get('/tenant/business/team?include_inactive=true')
      .then(r => setMembers(r.data || []))
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    if (canInvite) {
      api.get('/tenant/invitations').then(r => {
        setPendingInvites((r.data || []).filter(i => i.status === 'pending'));
      }).catch(() => {});
    }
  };
  useEffect(load, []);

  const handleRoleChange = async (id, tenant_role) => {
    setChangingRoleId(id);
    try {
      await api.patch(`/tenant/team/${id}/role`, { tenant_role });
      toast.success('Rol actualizado');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cambiar el rol'); }
    finally { setChangingRoleId(''); }
  };

  const handleStatusToggle = async (id, nextActive) => {
    if (!nextActive && !window.confirm('Esto desactiva el acceso de este miembro al sistema. ¿Continuar?')) return;
    setChangingStatusId(id);
    try {
      await api.patch(`/tenant/team/${id}/status`, { is_active: nextActive });
      toast.success(nextActive ? 'Miembro reactivado' : 'Miembro desactivado');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cambiar el estado'); }
    finally { setChangingStatusId(''); }
  };

  const startEdit = (m) => {
    setEditing(m.id);
    setForm({
      whatsapp_phone: (m.whatsapp_phone || '').replace(m.phone_country_code || tenantPhoneCode, ''),
      phone_country_code: m.phone_country_code || tenantPhoneCode,
      is_lead_eligible: m.is_lead_eligible || false,
      email: m.email || '',
    });
  };

  const save = async (id) => {
    try {
      const payload = { ...form };
      if (!canChangeEmail) delete payload.email;
      else if (!payload.email || !String(payload.email).trim()) delete payload.email;
      const { data } = await api.patch(`/tenant/business/team/${id}`, payload);
      if (data?.email_change && !data.email_change.unchanged) {
        toast.success(data.email_change.message || 'Correo de acceso actualizado.');
      } else {
        toast.success('Guardado');
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail || e)); }
  };

  const handleInvite = async () => {
    setInviting(true);
    try {
      const { data } = await api.post('/tenant/business/team/invite', inviteForm);
      setInviteResult(data);
      if (data.status === 'existing_updated') {
        toast.success(data.message);
        setShowInvite(false);
        setInviteResult(null);
        load();
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setInviting(false); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/invite/${token}`);
    toast.success('Enlace copiado');
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4" data-testid="team-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#64748B]">Configura tu equipo y quién recibe leads</p>
        {canInvite && (
          <Button onClick={() => { setShowInvite(true); setInviteResult(null); setInviteForm({ name: '', email: '', visible_role: 'Vendedor', phone_country_code: tenantPhoneCode, whatsapp_phone: '', is_lead_eligible_pending: false, delivery_method: 'copy_link' }); }}
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="invite-team-btn">
            <UserPlus className="w-4 h-4 mr-1" /> Invitar vendedor
          </Button>
        )}
      </div>

      {canHavePin && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-4" data-testid="supervisor-pin-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#0F172A]" />
              <div>
                <p className="text-sm font-semibold text-[#0F172A]">PIN de autorización</p>
                <p className="text-xs text-[#64748B]">
                  {myMember?.has_supervisor_pin ? 'Ya tienes un PIN configurado.' : 'Sin configurar.'}
                  {' '}Lo pedirá el Punto de Venta cuando un cajero necesite tu autorización para un reembolso o un descuento grande.
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowPinForm(v => !v)} data-testid="toggle-supervisor-pin">
              {myMember?.has_supervisor_pin ? 'Cambiar PIN' : 'Configurar PIN'}
            </Button>
          </div>
          {showPinForm && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Tu contraseña actual</label>
                <input type="password" value={pinForm.current_password}
                  onChange={e => setPinForm(f => ({ ...f, current_password: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="pin-current-password" />
              </div>
              <div>
                <label className="block text-xs text-[#64748B] mb-1">Nuevo PIN (4-6 dígitos)</label>
                <input type="password" inputMode="numeric" maxLength={6} value={pinForm.new_pin}
                  onChange={e => setPinForm(f => ({ ...f, new_pin: e.target.value.replace(/\D/g, '') }))}
                  className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="pin-new-value" />
              </div>
              <div className="sm:col-span-2">
                <Button onClick={savePin} disabled={savingPin || !pinForm.current_password || pinForm.new_pin.length < 4} data-testid="save-supervisor-pin">
                  Guardar PIN
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Nombre</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Rol</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">WhatsApp</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Recibe leads</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Estado</th>
              <th className="w-20" />
            </tr>
          </thead>
          <tbody>
            {members.map(m => (
              <tr key={m.id} className="border-b border-[#F1F5F9]" data-testid={`team-member-${m.id}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-[#0F172A]">{m.name || m.email}</p>
                  {editing === m.id && canChangeEmail ? (
                    <div className="mt-1">
                      <label className="text-[10px] uppercase tracking-wide text-[#94A3B8]">Correo de acceso</label>
                      <input type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        className="mt-0.5 w-full max-w-[240px] px-2 py-1.5 text-xs border border-[#E2E8F0] rounded"
                        placeholder="nuevo@correo.com" data-testid={`team-email-input-${m.id}`} />
                      <p className="text-[10px] text-amber-700 mt-0.5">Al cambiarlo, esa persona cierra sesión y entra con el correo nuevo.</p>
                    </div>
                  ) : (
                    <p className="text-xs text-[#94A3B8]">{m.email}</p>
                  )}
                </td>
                <td className="px-4 py-3 text-[#64748B]">
                  {canManageRoles && m.id !== user?.id && PERMISSION_ROLES.some(r => r.value === m.role_key) ? (
                    <select value={m.role_key} onChange={e => handleRoleChange(m.id, e.target.value)}
                      disabled={changingRoleId === m.id}
                      className="text-xs border border-[#E2E8F0] rounded px-1.5 py-1 bg-white" data-testid={`team-role-select-${m.id}`}>
                      {PERMISSION_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  ) : m.role}
                </td>
                <td className="px-4 py-3">
                  {editing === m.id ? (
                    <div className="flex gap-1 items-center">
                      <select value={form.phone_country_code} onChange={e => setForm(f => ({...f, phone_country_code: e.target.value}))}
                        className="text-xs border border-[#E2E8F0] rounded px-1.5 py-1.5 w-24">
                        {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </select>
                      <input value={form.whatsapp_phone || ''} onChange={e => setForm(f => ({...f, whatsapp_phone: e.target.value}))}
                        className="w-32 px-2 py-1.5 text-xs border border-[#E2E8F0] rounded" placeholder="5512345678"
                        data-testid="team-whatsapp-input" />
                    </div>
                  ) : (
                    <span className="text-xs">{m.whatsapp_phone ? <><Phone className="w-3 h-3 inline mr-1 text-emerald-500" />{m.whatsapp_phone}</> : <span className="text-[#94A3B8]">Sin WhatsApp</span>}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editing === m.id ? (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={form.is_lead_eligible} onChange={e => setForm(f => ({...f, is_lead_eligible: e.target.checked}))}
                        className="rounded border-[#E2E8F0]" data-testid="team-lead-eligible-toggle" />
                      <span className="text-xs text-[#64748B]">Disponible</span>
                    </label>
                  ) : (
                    m.is_lead_eligible ? <span className="text-xs text-emerald-600 font-medium">Sí</span> : <span className="text-xs text-[#94A3B8]">No</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {m.is_active ? (
                    <span className="text-xs text-emerald-600 font-medium">Activo</span>
                  ) : (
                    <span className="text-xs text-red-500 font-medium">Inactivo</span>
                  )}
                  {canManageRoles && m.role_key !== 'tenant_owner' && m.id !== user?.id && (
                    <button onClick={() => handleStatusToggle(m.id, !m.is_active)} disabled={changingStatusId === m.id}
                      className="ml-2 text-[11px] text-[#0F172A] underline hover:text-[#64748B]" data-testid={`team-status-toggle-${m.id}`}>
                      {changingStatusId === m.id ? '...' : m.is_active ? 'Desactivar' : 'Reactivar'}
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  {editing === m.id ? (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => save(m.id)} data-testid="team-save-btn"><Check className="w-3.5 h-3.5 text-emerald-600" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="w-3.5 h-3.5 text-[#94A3B8]" /></Button>
                    </div>
                  ) : (
                    (user?.tenant_role !== 'tenant_user' || m.id === user?.id) && (
                      <Button size="sm" variant="ghost" onClick={() => startEdit(m)} data-testid="team-edit-btn">Editar</Button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-[#94A3B8]">El WhatsApp del vendedor es donde le avisaremos cuando llegue un lead. Sin este dato no podemos notificar al vendedor.</p>

      {/* Pending invitations collapsible */}
      {canInvite && pendingInvites.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E2E8F0]">
          <button onClick={() => setShowPending(!showPending)} className="flex items-center gap-2 w-full px-4 py-3 text-sm font-medium text-[#0F172A]" data-testid="toggle-pending-invites">
            {showPending ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Invitaciones pendientes ({pendingInvites.length})
          </button>
          {showPending && (
            <div className="px-4 pb-3 space-y-2">
              {pendingInvites.map(inv => (
                <div key={inv.id} className="flex items-center justify-between p-2 bg-[#F8FAFC] rounded-lg" data-testid={`pending-invite-${inv.id}`}>
                  <div>
                    <p className="text-sm text-[#0F172A]">{inv.email}</p>
                    <p className="text-xs text-[#94A3B8]">{inv.tenant_role?.replace('tenant_', '')} · expira {new Date(inv.expires_at).toLocaleDateString('es-MX')}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => copyLink(inv.token)} data-testid={`copy-pending-${inv.id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Invite modal */}
      {/* La lista de invitaciones enviadas, que vivia en Configuracion. Va
          en el cuerpo de la pagina, NO dentro del modal de invitar: en el
          primer intento acabo dentro del dialogo y ademas leyendo una
          variable inexistente, asi que la pantalla se caia al abrirla. */}
      <InvitacionesEnviadas refrescar={members.length} />

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-md" data-testid="team-invite-dialog">
      <DialogHeader><DialogTitle className="text-lg font-bold text-[#0F172A]">Invitar vendedor</DialogTitle></DialogHeader>
          {inviteResult && inviteResult.status !== 'existing_updated' ? (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-800 font-medium">Invitación creada</p>
                <p className="text-xs text-emerald-700 mt-1">{inviteResult.message}</p>
              </div>
              {inviteResult.delivery_method && inviteResult.delivery_method !== 'copy_link' && DELIVERY_STATUS_LABELS[inviteResult.delivery_status] && (
                <div className={`p-2.5 rounded-lg border text-xs font-medium ${DELIVERY_STATUS_LABELS[inviteResult.delivery_status].className}`} data-testid="invite-delivery-status">
                  {DELIVERY_STATUS_LABELS[inviteResult.delivery_status].text}
                </div>
              )}
              <div className="flex items-center gap-2 p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                <code className="text-xs text-[#0F172A] flex-1 truncate">{window.location.origin}{inviteResult.invite_url}</code>
                <Button variant="ghost" size="sm" onClick={() => copyLink(inviteResult.token)} data-testid="copy-invite-link">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <Button onClick={() => { setShowInvite(false); setInviteResult(null); load(); }} className="w-full bg-[#0F172A] text-white">Cerrar</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#64748B] uppercase tracking-widest">Nombre</label>
                <input value={inviteForm.name} onChange={e => setInviteForm(f => ({...f, name: e.target.value}))}
                  className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" placeholder="Nombre del vendedor"
                  data-testid="invite-name-input" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#64748B] uppercase tracking-widest">Correo</label>
                <input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({...f, email: e.target.value}))}
                  className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" placeholder="correo@ejemplo.com"
                  data-testid="invite-email-input" required />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#64748B] uppercase tracking-widest">Rol</label>
                <select value={inviteForm.visible_role} onChange={e => setInviteForm(f => ({...f, visible_role: e.target.value}))}
                  className="w-full px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="invite-role-select">
                  {VISIBLE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#64748B] uppercase tracking-widest">WhatsApp (recomendado)</label>
                <div className="flex gap-2">
                  <select value={inviteForm.phone_country_code} onChange={e => setInviteForm(f => ({...f, phone_country_code: e.target.value}))}
                    className="text-xs border border-[#E2E8F0] rounded px-2 py-2 w-28" data-testid="invite-country-code">
                    {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                  <input value={inviteForm.whatsapp_phone} onChange={e => setInviteForm(f => ({...f, whatsapp_phone: e.target.value}))}
                    className="flex-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" placeholder="5512345678"
                    data-testid="invite-whatsapp-input" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={inviteForm.is_lead_eligible_pending}
                  onChange={e => setInviteForm(f => ({...f, is_lead_eligible_pending: e.target.checked}))}
                  className="rounded border-[#E2E8F0]" data-testid="invite-eligible-toggle" />
                <span className="text-sm text-[#0F172A]">Disponible para recibir leads</span>
              </label>
              <div className="space-y-2">
                <label className="text-xs font-semibold text-[#64748B] uppercase tracking-widest">¿Cómo se la enviamos?</label>
                <div className="grid grid-cols-3 gap-2">
                  {DELIVERY_METHODS.map(dm => {
                    const Icon = dm.icon;
                    const isWaDisabledHint = dm.value === 'whatsapp' && !inviteForm.whatsapp_phone;
                    return (
                      <button key={dm.value} type="button"
                        onClick={() => setInviteForm(f => ({...f, delivery_method: dm.value}))}
                        className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border text-xs font-medium transition-colors ${
                          inviteForm.delivery_method === dm.value
                            ? 'border-[#0F172A] bg-[#0F172A] text-white'
                            : 'border-[#E2E8F0] text-[#64748B] hover:border-[#94A3B8]'
                        }`}
                        data-testid={`invite-delivery-${dm.value}`}>
                        <Icon className="w-4 h-4" />
                        {dm.label}
                        {isWaDisabledHint && <span className="text-[10px] opacity-70">(sin WhatsApp cargado)</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Button onClick={handleInvite} disabled={inviting || !inviteForm.email || !inviteForm.name}
                className="w-full bg-[#0F172A] text-white" data-testid="invite-submit-btn">
                <Copy className="w-4 h-4 mr-1.5" />
                {inviting ? 'Creando...' : (
                  inviteForm.delivery_method === 'email' ? 'Crear y enviar por correo' :
                  inviteForm.delivery_method === 'whatsapp' ? 'Crear y enviar por WhatsApp' :
                  'Copiar enlace de invitación'
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
